import { useNavigate, useLocation } from "react-router-dom";
import { useMinimized } from "@/context/MinimizedContext";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { X, Maximize2, FileText, Wrench } from "lucide-react";
import { useState } from "react";

const KIND_ICON = {
  "jobcard-new": Wrench,
  "jobcard-edit": Wrench,
  "jobcard-page": Wrench,
  "txn": FileText,
  "txn-sale": FileText,
  "txn-purchase": FileText,
  "txn-return": FileText,
};

export default function MinimizedTaskbar() {
  const { windows, restore, close } = useMinimized();
  const navigate = useNavigate();
  const location = useLocation();
  const [confirmClose, setConfirmClose] = useState(null);

  if (!windows.length) return null;

  const onRestore = (w) => {
    restore(w.id);
    if (location.pathname + location.search !== w.path) navigate(w.path);
  };

  const onCloseClick = (w) => {
    if (w.dirty) setConfirmClose(w);
    else close(w.id);
  };

  const doConfirmedClose = () => {
    if (confirmClose) close(confirmClose.id);
    setConfirmClose(null);
  };

  return (
    <>
      <div
        data-testid="minimized-taskbar"
        className="fixed bottom-0 left-0 right-0 z-40 bg-card/95 backdrop-blur border-t border-border print:hidden"
      >
        <div className="flex items-center gap-2 px-3 py-2 overflow-x-auto">
          <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mr-2 whitespace-nowrap">
            Minimised ({windows.length})
          </span>
          {windows.map(w => {
            const Icon = KIND_ICON[w.kind] || FileText;
            return (
              <div
                key={w.id}
                data-testid={`minwin-${w.id}`}
                className="group flex items-center gap-2 border border-border bg-secondary rounded-sm pl-2 pr-1 h-9 shrink-0 hover:border-primary/60 transition-colors"
              >
                <button
                  type="button"
                  data-testid={`minwin-restore-${w.id}`}
                  onClick={() => onRestore(w)}
                  className="flex items-center gap-2 pr-1"
                  title="Click to restore"
                >
                  <Icon className="w-3.5 h-3.5 text-primary"/>
                  <div className="flex flex-col items-start leading-tight">
                    <span className="text-[11px] font-semibold uppercase tracking-wider">{w.label}</span>
                    <span className="text-[9px] text-muted-foreground font-mono">{w.ref || "—"}</span>
                  </div>
                </button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  data-testid={`minwin-close-${w.id}`}
                  onClick={() => onCloseClick(w)}
                  className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                  title="Close"
                >
                  <X className="w-3 h-3"/>
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  data-testid={`minwin-expand-${w.id}`}
                  onClick={() => onRestore(w)}
                  className="h-6 w-6 p-0 text-muted-foreground hover:text-primary"
                  title="Restore"
                >
                  <Maximize2 className="w-3 h-3"/>
                </Button>
              </div>
            );
          })}
        </div>
      </div>

      <AlertDialog open={!!confirmClose} onOpenChange={(o) => { if (!o) setConfirmClose(null); }}>
        <AlertDialogContent data-testid="minwin-discard-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved data?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmClose?.label} ({confirmClose?.ref || "New"}) has unsaved changes. Closing will discard them.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="minwin-discard-cancel">Keep</AlertDialogCancel>
            <AlertDialogAction data-testid="minwin-discard-confirm" onClick={doConfirmedClose} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Discard</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
